import { useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CleanedRecord {
  tax_id: string;
  date_recorded: string;
  instrument_type: string;
  county: string;
  original_name: string;
  first_name: string;
  last_name: string;
  seller_address: string;
  seller_city: string;
  seller_state: string;
  seller_zip: string;
  original_data?: string;
  issues_found?: string[];
}

interface DataCleanupTableProps {
  data: CleanedRecord[];
  onDataChange: (data: CleanedRecord[]) => void;
}

export function DataCleanupTable({ data, onDataChange }: DataCleanupTableProps) {
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);

  const handleCellEdit = (rowIndex: number, field: keyof CleanedRecord, value: string) => {
    const newData = [...data];
    newData[rowIndex] = { ...newData[rowIndex], [field]: value };
    onDataChange(newData);
  };

  const handleSwapNames = (rowIndex: number) => {
    const newData = [...data];
    const record = newData[rowIndex];
    newData[rowIndex] = { ...record, first_name: record.last_name, last_name: record.first_name };
    onDataChange(newData);
  };

  const renderEditableCell = (record: CleanedRecord, rowIndex: number, field: keyof CleanedRecord) => {
    const isEditing = editingCell?.row === rowIndex && editingCell?.field === field;
    const value = record[field] as string;

    if (isEditing) {
      return (
        <Input
          autoFocus
          value={value}
          onChange={(e) => handleCellEdit(rowIndex, field, e.target.value)}
          onBlur={() => setEditingCell(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setEditingCell(null);
            if (e.key === 'Escape') setEditingCell(null);
          }}
          className="h-8"
        />
      );
    }

    return (
      <div
        onClick={() => setEditingCell({ row: rowIndex, field })}
        className="cursor-pointer hover:bg-accent/50 px-2 py-1 rounded min-h-[32px]"
      >
        {value || <span className="text-muted-foreground italic">empty</span>}
      </div>
    );
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Tax ID</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>County</TableHead>
            <TableHead className="text-muted-foreground">Original Name</TableHead>
            <TableHead>First Name</TableHead>
            <TableHead>Last Name</TableHead>
            <TableHead className="w-[50px]">Swap</TableHead>
            <TableHead>Address</TableHead>
            <TableHead>City</TableHead>
            <TableHead className="w-[60px]">State</TableHead>
            <TableHead className="w-[80px]">Zip</TableHead>
            <TableHead className="w-[80px]">Issues</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((record, index) => (
            <TableRow key={index}>
              <TableCell className="font-mono text-xs">
                {renderEditableCell(record, index, 'tax_id')}
              </TableCell>
              <TableCell>{renderEditableCell(record, index, 'date_recorded')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'instrument_type')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'county')}</TableCell>
              <TableCell className="text-xs text-muted-foreground italic">{record.original_name}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'first_name')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'last_name')}</TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => handleSwapNames(index)}
                  title="Swap first ↔ last name"
                >
                  <ArrowLeftRight className="h-3 w-3" />
                </Button>
              </TableCell>
              <TableCell>{renderEditableCell(record, index, 'seller_address')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'seller_city')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'seller_state')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'seller_zip')}</TableCell>
              <TableCell>
                {record.issues_found && record.issues_found.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="cursor-help">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {record.issues_found.length}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[300px]">
                        <ul className="list-disc pl-4 space-y-1">
                          {record.issues_found.map((issue, i) => (
                            <li key={i} className="text-sm">{issue}</li>
                          ))}
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
